import { Link } from "react-router-dom";
import { ArrowRight, MapPinned, GraduationCap, Upload, Video, Building2, Stamp, Check, Languages } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionHeading } from "../components/site/SectionHeading";
import { Reveal } from "../components/site/Reveal";
import { FaqList } from "../components/site/FaqList";
import { ApplicantForm } from "../components/forms/ApplicantForm";
import { useSeo } from "../lib/useSeo";

const POINTS = [
  { icon: MapPinned, t: "What and where would you like to work?", d: "Tell us which job you are looking for and in which region of Germany you would like to work. If the location does not matter to you, that is fine too." },
  { icon: GraduationCap, t: "What have you learned?", d: "Describe your training, your qualifications and your work experience. Driving licences, certificates and language skills are important as well." },
  { icon: Upload, t: "How to submit your documents", d: "You can upload your CV and professional certificates directly in the form (PDF, JPG, PNG or DOCX). We do not need a passport copy or health documents at the beginning." },
  { icon: Video, t: "Introduction to employers", d: "If your profile matches a job, we introduce you to the employer. The first interview often takes place by video. We help you prepare for it." },
  { icon: Building2, t: "The employer decides", d: "Whether you are invited to an interview and hired is decided solely by the employer. We cannot guarantee that you will be hired." },
  { icon: Stamp, t: "Visa and recognition", d: "Before you start work, the required visa, the work permit and – depending on your profession – the recognition of your qualification must be in place. The authorities decide on this." },
];

const STEPS = [
  ["Register", "Fill in the registration form and upload your CV and certificates if you wish."],
  ["Profile and document review", "We look at your qualifications, experience and language skills and contact you if we have questions."],
  ["Search for suitable employers", "Our team in Germany looks for companies whose requirements match your profile."],
  ["Job interview", "If an employer is interested, we arrange an interview – usually by video first."],
  ["Employment contract", "If the employer chooses you, you receive an employment contract directly from that company."],
  ["Visa and recognition steps", "The required visa, work permit and, if necessary, professional recognition are applied for at the responsible authorities."],
  ["Start of work", "Once the required permits have been granted, you travel to Germany and start your job."],
];

const FAQ = [
  { q: "Does registration cost anything?", a: "No. Registering in our candidate pool is free of charge. The placement fee is agreed with the employer." },
  { q: "Which documents do I need to register?", a: "The details in the form are enough for your first registration. If available, you can upload your CV and professional certificates (e.g. diplomas, references, language certificates). We do not need a passport copy or health data for this." },
  { q: "Do I need to speak German?", a: "That depends on the profession, the employer and the legal requirements. In many professions – especially in healthcare and nursing – certain language certificates are mandatory. We will tell you openly what applies in your case." },
  { q: "How long does the process take?", a: "That depends on your profession, your country of origin, any required recognition and the processing time of the authorities and embassies. We therefore cannot promise fixed processing times, but we keep you informed." },
  { q: "Does PACTORA guarantee a visa or a job?", a: "No. Visas, residence permits and recognition are decided solely by the responsible authorities. The hiring decision is made by the employer. We support you with the preparation and organisation." },
  { q: "Who will be my employer?", a: "The company in Germany. You sign your employment contract directly with the employer, and the employer pays your salary. PACTORA is the placement agency, not your employer." },
];

const Hero = ({ register }) => (
  <PageHero
    testid={register ? "en-register-hero" : "en-applicants-hero"}
    eyebrow={register ? "Candidate registration" : "For applicants · English"}
    lines={register ? ["Register as", "an applicant."] : ["Work in Germany.", "We guide you."]}
    intro={register
      ? "Fill in the form – it takes about five minutes. Registration is free of charge. You may answer in English or German."
      : "Would you like to work in Germany and do you have vocational training or work experience? Register free of charge. We look for employers that match your profile and explain every step in plain language."}
    image={register ? undefined : "/img/gastro.webp"}
    imageAlt="Stock photo: chef in a restaurant kitchen"
    caption="Stock photo"
  >
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      {!register && (
        <Link to="/en/register" className="group inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--navy)] px-7 py-4 font-semibold text-white hover:bg-[color:var(--navy-deep)]" data-testid="en-hero-register-cta">
          Register now <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </Link>
      )}
      <Link to={register ? "/bewerber-registrierung" : "/bewerber"} hrefLang="de" lang="de" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--teal-ink)] underline-offset-4 hover:underline" data-testid="en-switch-german">
        <Languages size={16} /> Deutsche Version
      </Link>
    </div>
  </PageHero>
);

export function ApplicantsEn() {
  useSeo("Work in Germany – for applicants", "Would you like to work in Germany? Register free of charge with PACTORA Personalpartner. We introduce you to suitable employers and guide you through the next steps.", "/en/applicants", false, "en");
  return (
    <>
      <Hero />
      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="en-points">
        <div className="container-x">
          <SectionHeading eyebrow="How it works" title="What you should know" />
          <ol className="mt-14 grid gap-5 md:grid-cols-2">
            {POINTS.map((p, i) => (
              <Reveal key={p.t} as="li" delay={(i % 2) * 0.06}>
                <article className="flex h-full gap-5 rounded-3xl bg-white p-7 md:p-9" data-testid={`en-point-${i}`}>
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[color:var(--teal)] text-white"><p.icon size={22} /></span>
                  <div>
                    <h3 className="text-lg font-bold text-[color:var(--navy)]">{p.t}</h3>
                    <p className="mt-2 leading-relaxed text-slate-600">{p.d}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </ol>
          <Reveal>
            <div className="mt-10 rounded-3xl border border-[color:var(--teal)]/40 bg-white p-7 md:p-9">
              <h3 className="font-bold text-[color:var(--navy)]">To be honest</h3>
              <p className="mt-2 leading-relaxed text-slate-600">We do not promise a guaranteed visa, a guaranteed job or fixed processing times. The German language skills you need depend on the profession and the employer. In many professions, for example in nursing, certain language certificates are required by law. We will tell you openly what applies in your case.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="en-steps">
        <div className="container-x">
          <SectionHeading eyebrow="Your path" title="From registration to your first day at work" intro="The exact process may differ depending on your profession, your country of origin and your individual situation." />
          <ol className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(([t, d], i) => (
              <Reveal as="li" key={t} delay={(i % 4) * 0.05} className="bg-white p-7 md:p-8 min-h-[200px]">
                <span className="font-serif-accent text-5xl text-[color:var(--teal)]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 text-lg font-bold text-[color:var(--navy)]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
              </Reveal>
            ))}
            <li className="bg-[color:var(--navy)] p-7 md:p-8 flex items-end">
              <Link to="/en/register" className="group inline-flex items-center gap-2 font-semibold text-white" data-testid="en-steps-register-link">
                Start your registration <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </li>
          </ol>
        </div>
      </section>

      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="en-faq">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="Questions" title="Frequently asked questions" /></div>
          <div className="lg:col-span-8"><FaqList items={FAQ} prefix="en-faq" /></div>
        </div>
      </section>
    </>
  );
}

export function RegisterEn() {
  useSeo("Register as an applicant", "Register free of charge and work in Germany: PACTORA Personalpartner introduces your profile to suitable employers.", "/en/register", false, "en");
  return (
    <>
      <Hero register />
      <section className="bg-white pb-24 md:pb-32">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8"><ApplicantForm lang="en" /></div>
          <div className="lg:col-span-4">
            <aside className="rounded-[2rem] bg-[color:var(--mist)] p-8 lg:sticky lg:top-28">
              <h2 className="text-lg font-bold text-[color:var(--navy)]">Good to know</h2>
              <ul className="mt-5 space-y-4 text-sm text-slate-700">
                {[
                  "Your documents are stored privately and can only be viewed by our authorised team.",
                  "We do not need a passport copy or health data for your registration.",
                  "The hiring decision is made by the employer.",
                  "Visa, work permit and, if necessary, recognition are clarified before you start work.",
                  "Our privacy information is currently available in German only.",
                ].map((i) => (
                  <li key={i} className="flex gap-3"><Check size={16} className="mt-0.5 shrink-0 text-[color:var(--teal-ink)]" />{i}</li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
